import React, { useMemo, useState } from "react";
import {
  format,
  parseISO,
  differenceInCalendarDays,
  differenceInYears,
  differenceInMonths,
} from "date-fns";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronUp,
  ChevronDown,
  Info,
  CalendarIcon,
  X,
  Target,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCurrency, formatCurrencyWithCents } from "@/lib/currency";
import { describeTermsCode, lookupTermsCode } from "@/lib/par/termsCodeMap";
import type { PARInvoiceRow } from "@/lib/par/csvInvoiceParser";

interface VendorDrillDownProps {
  vendor: string;
  invoices: PARInvoiceRow[];
  loading?: boolean;
}

const fmtDate = (d: string | undefined | null): string => {
  if (!d) return "—";
  try {
    return format(parseISO(d), "MMM d, yyyy");
  } catch {
    return d;
  }
};

function calcDaysEarlyLate(inv: PARInvoiceRow): number | null {
  if (!inv.paid_date || !inv.due_date) return null;
  try {
    const paid = parseISO(inv.paid_date);
    const due = parseISO(inv.due_date);
    return differenceInCalendarDays(due, paid);
  } catch {
    return null;
  }
}

const PRESET_RATES = ["0.5", "1.0", "1.5", "2.0", "2.5", "3.0"];

type SortField =
  | "invoice_number"
  | "invoiced_date"
  | "amount_invoiced"
  | "discount_pct"
  | "days_early_late";
type SortDir = "asc" | "desc";

function SortableHeader({
  label,
  field,
  currentField,
  currentDir,
  onSort,
  className,
}: {
  label: string;
  field: SortField;
  currentField: SortField;
  currentDir: SortDir;
  onSort: (f: SortField) => void;
  className?: string;
}) {
  const active = currentField === field;
  return (
    <TableHead
      className={cn(
        "h-9 text-xs uppercase tracking-wide font-semibold text-muted-foreground whitespace-nowrap",
        className
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className="inline-flex items-center gap-1 cursor-pointer select-none hover:text-foreground transition-colors"
            onClick={() => onSort(field)}
          >
            {label}
            {active ? (
              currentDir === "asc" ? (
                <ChevronUp className="w-3.5 h-3.5 text-primary" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5 text-primary" />
              )
            ) : (
              <ChevronUp className="w-3.5 h-3.5 text-muted-foreground opacity-60" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent className="text-xs">
          Click to sort by {label}{" "}
          {active
            ? currentDir === "asc"
              ? "(currently ascending)"
              : "(currently descending)"
            : ""}
        </TooltipContent>
      </Tooltip>
    </TableHead>
  );
}

function StaticHeader({ label, className }: { label: string; className?: string }) {
  return (
    <TableHead
      className={cn(
        "h-9 text-xs uppercase tracking-wide font-semibold text-muted-foreground whitespace-nowrap",
        className
      )}
    >
      <span className="inline-flex items-center">{label}</span>
    </TableHead>
  );
}

export function VendorDrillDown({ vendor, invoices, loading }: VendorDrillDownProps) {
  const [simRateSelect, setSimRateSelect] = useState("1.0");
  const [simRateCustom, setSimRateCustom] = useState("");
  const [simDaysEarly, setSimDaysEarly] = useState("");
  const [sort, setSort] = useState<{ field: SortField; dir: SortDir }>({
    field: "invoiced_date",
    dir: "desc",
  });

  const [filterInvoiceNum, setFilterInvoiceNum] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState<Date | undefined>(undefined);
  const [filterDateTo, setFilterDateTo] = useState<Date | undefined>(undefined);
  const [filterTerms, setFilterTerms] = useState("all");
  const [filterAmountMin, setFilterAmountMin] = useState("");
  const [filterAmountMax, setFilterAmountMax] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const [dateFromOpen, setDateFromOpen] = useState(false);
  const [dateToOpen, setDateToOpen] = useState(false);

  const handleSort = (field: SortField) => {
    setSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { field, dir: "asc" }
    );
  };

  const simRate = useMemo(() => {
    if (simRateSelect === "custom") {
      return parseFloat(simRateCustom.replace(/[^0-9.]/g, "")) || 0;
    }
    return parseFloat(simRateSelect) || 0;
  }, [simRateSelect, simRateCustom]);

  const vendorInvoices = useMemo(
    () => invoices.filter((r) => r.vendor_name === vendor),
    [invoices, vendor]
  );

  const stats = useMemo(() => {
    const totalInvoices = vendorInvoices.length;
    const totalAmount = vendorInvoices.reduce((s, r) => s + r.amount_invoiced, 0);

    const actualSavings = vendorInvoices.reduce((s, r) => {
      const captured = r.paid_date && r.early_pay_due_date && r.paid_date <= r.early_pay_due_date;
      return s + (captured ? r.potential_savings : 0);
    }, 0);

    const bestDiscountPct = vendorInvoices.reduce((best, r) => {
      const pct = r.discount_pct ?? 0;
      return pct > best ? pct : best;
    }, 0);

    const invoicesAtBestRate = vendorInvoices.filter(
      (r) => bestDiscountPct > 0 && (r.discount_pct ?? 0) >= bestDiscountPct
    ).length;

    const potentialSavings =
      bestDiscountPct > 0
        ? vendorInvoices.reduce((s, r) => s + (r.amount_invoiced * bestDiscountPct) / 100, 0)
        : vendorInvoices.reduce((s, r) => s + r.potential_savings, 0);

    const missedSavings = Math.max(0, potentialSavings - actualSavings);

    const missedWithTerms = vendorInvoices.filter((r) => {
      if (!r.discount_pct || !r.early_pay_due_date || !r.paid_date) return false;
      return r.paid_date > r.early_pay_due_date;
    }).length;

    const noTerms = vendorInvoices.filter(
      (r) => r.discount_pct == null || r.discount_pct === 0
    ).length;

    const invoicesWithTerms = vendorInvoices.filter(
      (r) => r.discount_pct != null && r.discount_pct > 0
    );
    const avgDiscountPct =
      invoicesWithTerms.length > 0
        ? invoicesWithTerms.reduce((s, r) => s + (r.discount_pct ?? 0), 0) /
          invoicesWithTerms.length
        : 0;

    const daysValues = vendorInvoices.map(calcDaysEarlyLate).filter((v): v is number => v !== null);

    const avgDaysEarlyLate =
      daysValues.length > 0 ? daysValues.reduce((s, v) => s + v, 0) / daysValues.length : null;

    const medianDaysEarlyLate = (() => {
      if (daysValues.length === 0) return null;
      const sorted = [...daysValues].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
    })();

    const termsDistribution = (() => {
      const map = new Map<string, number>();
      for (const r of vendorInvoices) {
        const key = r.terms || "No terms";
        map.set(key, (map.get(key) ?? 0) + 1);
      }
      return Array.from(map.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);
    })();

    // Relationship timeline
    const firstInvoiceDate = vendorInvoices.reduce(
      (earliest, r) => {
        if (!r.invoiced_date) return earliest;
        return !earliest || r.invoiced_date < earliest ? r.invoiced_date : earliest;
      },
      null as string | null
    );

    const lastInvoiceDate = vendorInvoices.reduce(
      (latest, r) => {
        if (!r.invoiced_date) return latest;
        return !latest || r.invoiced_date > latest ? r.invoiced_date : latest;
      },
      null as string | null
    );

    const relationshipYears = firstInvoiceDate
      ? differenceInYears(new Date(), parseISO(firstInvoiceDate))
      : null;

    const avgAnnualSpend = (() => {
      if (!firstInvoiceDate) return null;
      const end = lastInvoiceDate ? parseISO(lastInvoiceDate) : new Date();
      const months = differenceInMonths(end, parseISO(firstInvoiceDate));
      if (months < 1) return totalAmount;
      return totalAmount / (months / 12);
    })();

    // Discount capture rate
    const eligibleInvoices = vendorInvoices.filter(
      (r) => r.discount_pct && r.discount_pct > 0 && r.early_pay_due_date
    );
    const capturedInvoices = eligibleInvoices.filter(
      (r) => r.paid_date && r.paid_date <= r.early_pay_due_date!
    );
    const captureRate =
      eligibleInvoices.length > 0
        ? (capturedInvoices.length / eligibleInvoices.length) * 100
        : null;

    // On-time payment rate
    const paidWithDueDate = vendorInvoices.filter((r) => r.paid_date && r.due_date);
    const onTimeInvoices = paidWithDueDate.filter((r) => r.paid_date! <= r.due_date!);
    const onTimeRate =
      paidWithDueDate.length > 0 ? (onTimeInvoices.length / paidWithDueDate.length) * 100 : null;

    // Annual spend by year
    const spendByYear = (() => {
      const map = new Map<string, number>();
      for (const r of vendorInvoices) {
        if (!r.invoiced_date) continue;
        try {
          const year = format(parseISO(r.invoiced_date), "yyyy");
          map.set(year, (map.get(year) ?? 0) + r.amount_invoiced);
        } catch {
          /* skip rows with unparseable dates */
        }
      }
      return Array.from(map.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([year, amount]) => ({ year, amount }));
    })();

    const dataSpanMonths = (() => {
      if (!firstInvoiceDate) return null;
      const end = lastInvoiceDate ? parseISO(lastInvoiceDate) : new Date();
      return differenceInMonths(end, parseISO(firstInvoiceDate));
    })();

    return {
      totalInvoices,
      totalAmount,
      actualSavings,
      bestDiscountPct,
      invoicesAtBestRate,
      potentialSavings,
      missedSavings,
      missedWithTerms,
      noTerms,
      avgDiscountPct,
      avgDaysEarlyLate,
      medianDaysEarlyLate,
      termsDistribution,
      firstInvoiceDate,
      lastInvoiceDate,
      relationshipYears,
      avgAnnualSpend,
      dataSpanMonths,
      captureRate,
      eligibleCount: eligibleInvoices.length,
      capturedCount: capturedInvoices.length,
      onTimeRate,
      spendByYear,
    };
  }, [vendorInvoices]);

  const uniqueTerms = useMemo(() => {
    const s = new Set<string>();
    for (const r of vendorInvoices) {
      if (r.terms) s.add(r.terms);
    }
    return Array.from(s).sort();
  }, [vendorInvoices]);

  const displayedInvoices = useMemo(() => {
    let list = vendorInvoices;

    if (filterInvoiceNum.trim()) {
      const q = filterInvoiceNum.trim().toLowerCase();
      list = list.filter((r) => r.invoice_number && r.invoice_number.toLowerCase().includes(q));
    }

    if (filterDateFrom) {
      const from = format(filterDateFrom, "yyyy-MM-dd");
      list = list.filter((r) => r.invoiced_date && r.invoiced_date >= from);
    }
    if (filterDateTo) {
      const to = format(filterDateTo, "yyyy-MM-dd");
      list = list.filter((r) => r.invoiced_date && r.invoiced_date <= to);
    }

    if (filterTerms !== "all") {
      if (filterTerms === "no_terms") {
        list = list.filter((r) => !r.terms || r.terms === "");
      } else {
        list = list.filter((r) => r.terms === filterTerms);
      }
    }

    if (filterAmountMin.trim()) {
      const min = parseFloat(filterAmountMin.replace(/[^0-9.]/g, ""));
      if (!isNaN(min)) list = list.filter((r) => r.amount_invoiced >= min);
    }
    if (filterAmountMax.trim()) {
      const max = parseFloat(filterAmountMax.replace(/[^0-9.]/g, ""));
      if (!isNaN(max)) list = list.filter((r) => r.amount_invoiced <= max);
    }

    if (filterStatus !== "all") {
      if (filterStatus === "early") {
        list = list.filter(
          (r) => r.paid_date && r.early_pay_due_date && r.paid_date <= r.early_pay_due_date
        );
      } else if (filterStatus === "standard") {
        list = list.filter(
          (r) => r.paid_date && !(r.early_pay_due_date && r.paid_date <= r.early_pay_due_date)
        );
      } else if (filterStatus === "unpaid") {
        list = list.filter((r) => !r.paid_date);
      }
    }

    list = [...list].sort((a, b) => {
      let cmp = 0;
      if (sort.field === "invoice_number") {
        cmp = (a.invoice_number || "").localeCompare(b.invoice_number || "");
      } else if (sort.field === "invoiced_date") {
        cmp = (a.invoiced_date || "").localeCompare(b.invoiced_date || "");
      } else if (sort.field === "amount_invoiced") {
        cmp = a.amount_invoiced - b.amount_invoiced;
      } else if (sort.field === "discount_pct") {
        cmp = (a.discount_pct ?? 0) - (b.discount_pct ?? 0);
      } else if (sort.field === "days_early_late") {
        const aVal = calcDaysEarlyLate(a) ?? -Infinity;
        const bVal = calcDaysEarlyLate(b) ?? -Infinity;
        cmp = aVal - bVal;
      }
      return sort.dir === "asc" ? cmp : -cmp;
    });

    return list;
  }, [
    vendorInvoices,
    sort,
    filterInvoiceNum,
    filterDateFrom,
    filterDateTo,
    filterTerms,
    filterAmountMin,
    filterAmountMax,
    filterStatus,
  ]);

  const briefItems = useMemo(
    () =>
      [
        stats.firstInvoiceDate && {
          label: "Customer Since",
          value: format(parseISO(stats.firstInvoiceDate), "MMM yyyy"),
          sub:
            stats.relationshipYears === 0
              ? "< 1 year"
              : `${stats.relationshipYears} yr${stats.relationshipYears !== 1 ? "s" : ""}`,
          color: "text-foreground",
          tooltip: null,
        },
        stats.avgAnnualSpend != null && {
          label: "Avg Annual Spend",
          value: formatCurrency(stats.avgAnnualSpend),
          sub: "per year",
          color: "text-foreground",
          tooltip:
            stats.dataSpanMonths !== null && stats.dataSpanMonths < 12
              ? `Based on only ${stats.dataSpanMonths} month${stats.dataSpanMonths !== 1 ? "s" : ""} of data — annualized figure may not reflect a full year`
              : null,
        },
        stats.bestDiscountPct > 0 && {
          label: "Best Discount Seen",
          value: `${stats.bestDiscountPct.toFixed(1)}%`,
          sub: "highest rate achieved",
          color: "text-primary",
          tooltip: null,
        },
        stats.captureRate !== null && {
          label: "Capture Rate",
          value: `${stats.captureRate.toFixed(0)}%`,
          sub: `${stats.capturedCount} of ${stats.eligibleCount} eligible`,
          color:
            stats.captureRate >= 80
              ? "text-success"
              : stats.captureRate >= 50
                ? "text-amber-600"
                : "text-destructive",
          tooltip: null,
        },
        stats.avgDaysEarlyLate !== null && {
          label: "Payment Behavior",
          value: `${stats.avgDaysEarlyLate > 0 ? "+" : ""}${stats.avgDaysEarlyLate.toFixed(0)}d`,
          sub: stats.avgDaysEarlyLate >= 0 ? "early on average" : "late on average",
          color: stats.avgDaysEarlyLate >= 0 ? "text-success" : "text-destructive",
          tooltip: null,
        },
        stats.onTimeRate !== null && {
          label: "On-time Rate",
          value: `${stats.onTimeRate.toFixed(0)}%`,
          sub: "paid by due date",
          color:
            stats.onTimeRate >= 90
              ? "text-success"
              : stats.onTimeRate >= 70
                ? "text-amber-600"
                : "text-destructive",
          tooltip: null,
        },
        stats.bestDiscountPct > 0 &&
          stats.avgAnnualSpend != null && {
            label: "Annual Target Savings",
            value: formatCurrency((stats.avgAnnualSpend * stats.bestDiscountPct) / 100),
            sub: `at ${stats.bestDiscountPct.toFixed(1)}% on all invoices`,
            color: "text-foreground",
            tooltip:
              stats.dataSpanMonths !== null && stats.dataSpanMonths < 12
                ? `Extrapolated from ${stats.dataSpanMonths} month${stats.dataSpanMonths !== 1 ? "s" : ""} of data`
                : null,
          },
      ].filter(Boolean) as {
        label: string;
        value: string;
        sub: string;
        color: string;
        tooltip: string | null;
      }[],
    [stats]
  );

  if (loading && vendorInvoices.length === 0) {
    return (
      <div className="space-y-4 p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="p-4">
              <div className="h-3 w-20 bg-muted animate-pulse rounded mb-2" />
              <div className="h-7 w-24 bg-muted animate-pulse rounded mb-1" />
            </Card>
          ))}
        </div>
        <Card className="p-4">
          <div className="h-3 w-32 bg-muted animate-pulse rounded mb-3" />
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-8 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </Card>
      </div>
    );
  }

  if (!loading && vendorInvoices.length === 0) {
    return (
      <div className="py-16 text-center text-sm text-muted-foreground">
        No invoices found for {vendor}.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Negotiation Brief */}
      {briefItems.length > 0 && (
        <Card className="p-4 bg-primary/5 border-primary/20 space-y-3">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-primary shrink-0" />
            <span className="text-sm font-semibold text-foreground uppercase tracking-wide">
              Negotiation Brief
            </span>
            <span className="text-xs text-muted-foreground">
              — key facts for your discussion with {vendor}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
            {briefItems.map((item) => (
              <div key={item.label}>
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold leading-tight">
                  {item.label}
                </p>
                <div className="flex items-center gap-1 mt-0.5">
                  <p className={cn("text-lg font-bold leading-tight", item.color)}>{item.value}</p>
                  {item.tooltip && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3.5 h-3.5 text-amber-500 cursor-help shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[220px] text-xs">
                        {item.tooltip}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{item.sub}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Summary metric cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Card className="p-4">
          <span className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
            Total Invoices
          </span>
          <p className="text-2xl font-bold text-foreground leading-tight mt-0.5">
            {stats.totalInvoices.toLocaleString()}
          </p>
        </Card>

        <Card className="p-4">
          <span className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
            Total Amount
          </span>
          <p className="text-2xl font-bold text-foreground leading-tight mt-0.5">
            {formatCurrency(stats.totalAmount)}
          </p>
          <p className="text-sm text-muted-foreground">invoiced</p>
        </Card>

        {stats.avgDaysEarlyLate !== null && (
          <Card className="p-4">
            <span className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
              Avg Days Early / Late
            </span>
            <p
              className={cn(
                "text-2xl font-bold leading-tight mt-0.5",
                stats.avgDaysEarlyLate > 0
                  ? "text-success"
                  : stats.avgDaysEarlyLate < 0
                    ? "text-destructive"
                    : "text-foreground"
              )}
            >
              {stats.avgDaysEarlyLate > 0 ? "+" : ""}
              {stats.avgDaysEarlyLate.toFixed(1)} days
            </p>
            <p className="text-sm text-muted-foreground">
              {stats.avgDaysEarlyLate >= 0 ? "paid early on average" : "paid late on average"}
              {stats.medianDaysEarlyLate !== null && (
                <>
                  {" "}
                  · median {stats.medianDaysEarlyLate > 0 ? "+" : ""}
                  {stats.medianDaysEarlyLate.toFixed(0)}
                </>
              )}
            </p>
          </Card>
        )}
      </div>

      {/* Annual Spend Trend */}
      {stats.spendByYear.length > 1 && (
        <Card className="p-4 space-y-3">
          <span className="text-sm font-semibold text-foreground uppercase tracking-wide">
            Annual Spend Trend
          </span>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.spendByYear} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <XAxis dataKey="year" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: number) =>
                    v >= 1_000_000
                      ? `$${(v / 1_000_000).toFixed(1)}M`
                      : v >= 1_000
                        ? `$${(v / 1_000).toFixed(0)}K`
                        : `$${v}`
                  }
                  width={60}
                />
                <RechartsTooltip
                  formatter={(value: number) => [formatCurrencyWithCents(value), "Spend"]}
                  contentStyle={{ fontSize: 12, borderRadius: 6 }}
                  cursor={{ fill: "hsl(var(--muted))" }}
                />
                <Bar dataKey="amount" radius={[3, 3, 0, 0]} fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Payment Terms Distribution */}
      {stats.termsDistribution.length > 0 && (
        <Card className="p-4 space-y-2">
          <span className="text-sm font-semibold text-foreground uppercase tracking-wide">
            Payment Terms Distribution
          </span>
          <div className="flex flex-wrap gap-2 pt-1">
            {stats.termsDistribution.map(([term, count]) => {
              const hasDiscount = term !== "No terms" && lookupTermsCode(term)?.discountPct != null;
              const label = term === "No terms" ? term : describeTermsCode(term);
              return (
                <div key={term} className="flex items-center gap-2">
                  <span
                    className={cn(
                      "inline-block px-2 py-1 rounded text-xs font-medium border leading-tight",
                      hasDiscount
                        ? "bg-success/10 text-success border-success/20"
                        : "bg-muted text-muted-foreground border-border"
                    )}
                  >
                    {label}
                  </span>
                  <span className="text-sm text-muted-foreground">×{count}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Savings Opportunity */}
      {stats.bestDiscountPct > 0 && (
        <Card className="p-4 space-y-3">
          <div>
            <span className="text-sm font-semibold text-foreground uppercase tracking-wide">
              Savings Opportunity
            </span>
            <p className="text-sm text-muted-foreground mt-1">
              If all {stats.totalInvoices} invoices captured the best discount rate of{" "}
              <span className="font-semibold text-foreground">
                {stats.bestDiscountPct.toFixed(1)}%
              </span>{" "}
              seen across this vendor's invoices:
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="flex items-center gap-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
                  Best Discount %
                </p>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3.5 h-3.5 text-muted-foreground/60 cursor-help shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[220px] text-xs">
                    The highest discount rate achieved across {stats.invoicesAtBestRate} invoice
                    {stats.invoicesAtBestRate !== 1 ? "s" : ""} — your savings if you achieved this
                    rate uniformly across all invoices from {vendor}.
                  </TooltipContent>
                </Tooltip>
              </div>
              <p className="text-xl font-bold text-primary mt-0.5">
                {stats.bestDiscountPct.toFixed(1)}%
              </p>
              <p className="text-sm text-muted-foreground">highest rate seen</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
                Potential Savings
              </p>
              <p className="text-xl font-bold text-foreground mt-0.5">
                {formatCurrencyWithCents(stats.potentialSavings)}
              </p>
              <p className="text-sm text-muted-foreground">if best terms applied to all</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
                Uplift Available
              </p>
              <p className="text-xl font-bold text-destructive mt-0.5">
                {formatCurrencyWithCents(stats.missedSavings)}
              </p>
              <p className="text-sm text-muted-foreground">potential minus actual</p>
            </div>
          </div>
        </Card>
      )}

      {/* Scenario Planner */}
      <Card className="p-4 space-y-3">
        <div>
          <span className="text-sm font-semibold text-foreground uppercase tracking-wide">
            Scenario Planner
          </span>
          <p className="text-sm text-muted-foreground mt-1">
            Model what savings look like if you negotiate a new discount rate with {vendor} — useful
            for building your ask before the meeting.
          </p>
        </div>

        <div className="flex items-end gap-3 flex-wrap">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
              Discount Rate
            </Label>
            <Select value={simRateSelect} onValueChange={setSimRateSelect}>
              <SelectTrigger className="h-8 w-32 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRESET_RATES.map((r) => (
                  <SelectItem key={r} value={r} className="text-sm">
                    {r}%
                  </SelectItem>
                ))}
                <SelectItem value="custom" className="text-sm">
                  Custom…
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {simRateSelect === "custom" && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
                Custom %
              </Label>
              <div className="relative">
                <Input
                  value={simRateCustom}
                  onChange={(e) => setSimRateCustom(e.target.value)}
                  placeholder="e.g. 1.75"
                  className="h-8 w-24 text-sm pr-5"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  %
                </span>
              </div>
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide font-semibold flex items-center gap-1">
              Days Paid Early
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3.5 h-3.5 text-muted-foreground/60 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[240px] text-xs">
                  Working capital needed = outstanding balance × (APR / 100) × (days / 365). Enter
                  days to see how much capital you need to deploy.
                </TooltipContent>
              </Tooltip>
            </Label>
            <div className="relative">
              <Input
                value={simDaysEarly}
                onChange={(e) => setSimDaysEarly(e.target.value)}
                placeholder="30"
                className="h-8 w-24 text-sm pr-6"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                d
              </span>
            </div>
          </div>
        </div>

        {simRate > 0 &&
          (() => {
            const days = parseFloat(simDaysEarly) || 0;
            const wc = days > 0 ? stats.totalAmount * (simRate / 100) * (days / 365) : null;
            return wc !== null ? (
              <div className="bg-muted/50 rounded-md px-3 py-2 flex items-center gap-3">
                <Info className="w-4 h-4 text-muted-foreground shrink-0" />
                <p className="text-sm text-muted-foreground">
                  Paying <span className="font-semibold text-foreground">{days} days early</span> at{" "}
                  <span className="font-semibold text-foreground">{simRate.toFixed(1)}%</span> APR
                  requires approx.{" "}
                  <span className="font-semibold text-foreground">
                    {formatCurrencyWithCents(wc)}
                  </span>{" "}
                  in working capital deployed against {formatCurrency(stats.totalAmount)} in
                  outstanding invoices.
                </p>
              </div>
            ) : null;
          })()}

        {simRate > 0 && (
          <div className="grid grid-cols-3 gap-4 pt-1">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
                Actual Savings Already Captured
              </p>
              <p className="text-xl font-bold text-success mt-0.5">
                {formatCurrencyWithCents(stats.actualSavings)}
              </p>
              <p className="text-sm text-muted-foreground">paid within early pay window</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
                Simulated Savings
              </p>
              <p className="text-xl font-bold text-foreground mt-0.5">
                {formatCurrencyWithCents(
                  vendorInvoices.reduce((s, r) => s + (r.amount_invoiced * simRate) / 100, 0)
                )}
              </p>
              <p className="text-sm text-muted-foreground">at {simRate.toFixed(1)}% rate</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
                Additional Opportunity
              </p>
              {(() => {
                const simSavings = vendorInvoices.reduce(
                  (s, r) => s + (r.amount_invoiced * simRate) / 100,
                  0
                );
                const existingTermsPotential = vendorInvoices.reduce(
                  (s, r) => s + r.potential_savings,
                  0
                );
                const delta = simSavings - existingTermsPotential;
                return (
                  <p
                    className={`text-xl font-bold mt-0.5 ${delta >= 0 ? "text-success" : "text-destructive"}`}
                  >
                    {delta >= 0 ? "+" : ""}
                    {formatCurrencyWithCents(Math.abs(delta))}
                  </p>
                );
              })()}
              <p className="text-sm text-muted-foreground">vs existing terms (if all captured)</p>
            </div>
          </div>
        )}
      </Card>

      {/* Invoice table */}
      <Card className="overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border flex items-center justify-between gap-3">
          <span className="text-sm font-semibold text-foreground uppercase tracking-wide whitespace-nowrap">
            All Invoices — {vendor}
          </span>
        </div>

        {(() => {
          const anyFilter =
            filterInvoiceNum.trim() !== "" ||
            filterDateFrom !== undefined ||
            filterDateTo !== undefined ||
            filterTerms !== "all" ||
            filterAmountMin.trim() !== "" ||
            filterAmountMax.trim() !== "" ||
            filterStatus !== "all";
          return (
            <div className="px-4 py-2 border-b border-border bg-muted/20 flex items-center gap-2 flex-wrap">
              <Input
                value={filterInvoiceNum}
                onChange={(e) => setFilterInvoiceNum(e.target.value)}
                placeholder="Invoice #…"
                className="h-7 text-xs w-32"
              />

              <span className="text-muted-foreground/50 text-xs select-none">|</span>

              <Popover open={dateFromOpen} onOpenChange={setDateFromOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-xs px-2 font-normal">
                    {filterDateFrom ? (
                      format(filterDateFrom, "MM/dd/yy")
                    ) : (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <CalendarIcon className="w-3 h-3" />
                        From
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filterDateFrom}
                    onSelect={(d) => {
                      setFilterDateFrom(d);
                      setDateFromOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <span className="text-muted-foreground text-xs">–</span>
              <Popover open={dateToOpen} onOpenChange={setDateToOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-xs px-2 font-normal">
                    {filterDateTo ? (
                      format(filterDateTo, "MM/dd/yy")
                    ) : (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <CalendarIcon className="w-3 h-3" />
                        To
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filterDateTo}
                    onSelect={(d) => {
                      setFilterDateTo(d);
                      setDateToOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {(filterDateFrom || filterDateTo) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => {
                    setFilterDateFrom(undefined);
                    setFilterDateTo(undefined);
                  }}
                >
                  <X className="w-3 h-3" />
                </Button>
              )}

              <span className="text-muted-foreground/50 text-xs select-none">|</span>

              <Select value={filterTerms} onValueChange={setFilterTerms}>
                <SelectTrigger className="h-7 text-xs w-36">
                  <SelectValue placeholder="All terms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">
                    All terms
                  </SelectItem>
                  <SelectItem value="no_terms" className="text-xs">
                    No terms
                  </SelectItem>
                  {uniqueTerms.map((t) => (
                    <SelectItem key={t} value={t} className="text-xs">
                      {describeTermsCode(t)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <span className="text-muted-foreground/50 text-xs select-none">|</span>

              <Input
                value={filterAmountMin}
                onChange={(e) => setFilterAmountMin(e.target.value)}
                placeholder="Min $"
                className="h-7 text-xs w-20"
              />
              <span className="text-muted-foreground text-xs">–</span>
              <Input
                value={filterAmountMax}
                onChange={(e) => setFilterAmountMax(e.target.value)}
                placeholder="Max $"
                className="h-7 text-xs w-20"
              />

              <span className="text-muted-foreground/50 text-xs select-none">|</span>

              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-7 text-xs w-32">
                  <SelectValue placeholder="All status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">
                    All
                  </SelectItem>
                  <SelectItem value="early" className="text-xs">
                    Early paid
                  </SelectItem>
                  <SelectItem value="standard" className="text-xs">
                    Standard paid
                  </SelectItem>
                  <SelectItem value="unpaid" className="text-xs">
                    Unpaid
                  </SelectItem>
                </SelectContent>
              </Select>

              {anyFilter && (
                <>
                  <span className="flex-1" />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setFilterInvoiceNum("");
                      setFilterDateFrom(undefined);
                      setFilterDateTo(undefined);
                      setFilterTerms("all");
                      setFilterAmountMin("");
                      setFilterAmountMax("");
                      setFilterStatus("all");
                    }}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Clear all
                  </Button>
                </>
              )}
            </div>
          );
        })()}

        {displayedInvoices.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No invoices match current filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <SortableHeader
                    label="Invoice #"
                    field="invoice_number"
                    currentField={sort.field}
                    currentDir={sort.dir}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Invoice Date"
                    field="invoiced_date"
                    currentField={sort.field}
                    currentDir={sort.dir}
                    onSort={handleSort}
                  />
                  <StaticHeader label="Terms" />
                  <SortableHeader
                    label="Amount"
                    field="amount_invoiced"
                    currentField={sort.field}
                    currentDir={sort.dir}
                    onSort={handleSort}
                    className="text-right"
                  />
                  <SortableHeader
                    label="Discount %"
                    field="discount_pct"
                    currentField={sort.field}
                    currentDir={sort.dir}
                    onSort={handleSort}
                    className="text-right"
                  />
                  <StaticHeader label="Actual Savings Captured" className="text-right" />
                  <SortableHeader
                    label="Days Early/Late"
                    field="days_early_late"
                    currentField={sort.field}
                    currentDir={sort.dir}
                    onSort={handleSort}
                    className="text-right"
                  />
                  <StaticHeader label="Status" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedInvoices.map((inv, idx) => {
                  const captured =
                    inv.paid_date &&
                    inv.early_pay_due_date &&
                    inv.paid_date <= inv.early_pay_due_date;
                  const isBestTerms =
                    stats.bestDiscountPct > 0 && (inv.discount_pct ?? 0) >= stats.bestDiscountPct;
                  const daysEarlyLate = calcDaysEarlyLate(inv);
                  const hasDiscount = inv.terms
                    ? lookupTermsCode(inv.terms)?.discountPct != null
                    : false;

                  return (
                    <TableRow key={inv.db_id ?? `${inv.invoice_number}-${idx}`} className="text-sm">
                      <TableCell className="font-mono text-xs">
                        {inv.invoice_number || "—"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {fmtDate(inv.invoiced_date)}
                      </TableCell>
                      <TableCell>
                        {inv.terms ? (
                          <span
                            className={cn(
                              "inline-block px-1.5 py-0.5 rounded text-xs font-medium border leading-tight",
                              hasDiscount
                                ? "bg-success/10 text-success border-success/20"
                                : "bg-muted text-muted-foreground border-border"
                            )}
                          >
                            {describeTermsCode(inv.terms)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums text-sm">
                        {formatCurrency(inv.amount_invoiced)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {inv.discount_pct != null && inv.discount_pct > 0 ? (
                          <span className={isBestTerms ? "text-primary font-semibold" : ""}>
                            {inv.discount_pct.toFixed(1)}%
                            {isBestTerms && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <TrendingUp className="inline-block w-3.5 h-3.5 ml-1 text-primary cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent className="text-xs">
                                  Best discount rate seen for {vendor}
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {captured ? (
                          <span className="text-success font-medium">
                            {formatCurrencyWithCents(inv.potential_savings)}
                          </span>
                        ) : inv.discount_pct != null && inv.discount_pct > 0 ? (
                          <span className="text-destructive">
                            {formatCurrencyWithCents(inv.potential_savings)} missed
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {daysEarlyLate !== null ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className={cn(
                                  "font-medium cursor-help",
                                  daysEarlyLate > 0
                                    ? "text-success"
                                    : daysEarlyLate < 0
                                      ? "text-destructive"
                                      : "text-muted-foreground"
                                )}
                              >
                                {daysEarlyLate > 0 ? "+" : ""}
                                {daysEarlyLate}d
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="text-xs">
                              {daysEarlyLate > 0
                                ? `Paid ${daysEarlyLate} day${daysEarlyLate !== 1 ? "s" : ""} before due date`
                                : daysEarlyLate < 0
                                  ? `Paid ${Math.abs(daysEarlyLate)} day${Math.abs(daysEarlyLate) !== 1 ? "s" : ""} after due date`
                                  : "Paid exactly on due date"}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {captured ? (
                          <Badge
                            variant="outline"
                            className="text-xs border-success/30 text-success bg-success/5 gap-1"
                          >
                            <TrendingUp className="w-3 h-3" />
                            Early paid
                          </Badge>
                        ) : inv.paid_date ? (
                          <Badge variant="outline" className="text-xs text-muted-foreground gap-1">
                            <Minus className="w-3 h-3" />
                            Paid standard
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-xs border-amber-400/40 text-amber-600 bg-amber-50 gap-1"
                          >
                            <TrendingDown className="w-3 h-3" />
                            Unpaid
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
